import { AnimatePresence, motion } from "framer-motion";
import { X, Clock, AlertTriangle, ShieldCheck } from "lucide-react";
import { imageUrl } from "../lib/api";
import { EnquiryForm } from "./EnquiryForm";

export const BikeDetailModal = ({ product, onClose }) => (
  <AnimatePresence>
    {product && (
      <motion.div
        data-testid="product-detail-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-forest-deep/92 p-4 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative my-8 grid w-full max-w-5xl overflow-hidden border border-border bg-charcoal lg:grid-cols-2"
        >
          <button
            data-testid="product-detail-close"
            onClick={onClose}
            className="absolute right-4 top-4 z-20 border border-border bg-forest-deep/90 p-2 text-sand transition-colors hover:border-brass hover:text-brass"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative flex items-center justify-center bg-[#FAFAFC] p-6">
            <img src={imageUrl(product.image)} alt={product.name} className="max-h-[440px] w-full object-contain" />
            <span className="absolute left-4 top-4 bg-forest-deep px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.2em] text-volt uppercase">
              {product.badge}
            </span>
            <span className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-cyanx px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.15em] text-forest-deep uppercase">
              <Clock className="h-3 w-3" strokeWidth={2.5} />
              Lead time: {product.waiting_time}
            </span>
          </div>

          <div className="flex flex-col p-7 lg:p-9">
            <p className="mono-label">{product.category}</p>
            <h3 className="mt-2 font-serif text-3xl font-black uppercase leading-none text-bone lg:text-4xl">
              {product.name}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-sand">{product.description}</p>

            <p data-testid="detail-price-note" className="mt-5 font-serif text-2xl font-bold uppercase text-volt">
              {product.price_note}
            </p>
            <p data-testid="detail-price-promise" className="mt-1.5 font-mono text-[9px] font-medium tracking-[0.15em] text-sand/60 uppercase">
              One honest price — UK delivery included, no hidden costs
            </p>

            <div className="mt-4 max-h-48 overflow-y-auto border border-border">
              {Object.entries(product.specs || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border px-4 py-2 last:border-0">
                  <span className="font-mono text-[10px] tracking-[0.15em] text-sand/70 uppercase">{k}</span>
                  <span className="text-right font-mono text-[11px] font-medium text-bone">{v}</span>
                </div>
              ))}
            </div>

            {product.accessories?.length > 0 && (
              <div className="mt-4">
                <p className="font-mono text-[9px] tracking-[0.2em] text-sand/50 uppercase">Add on enquiry</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {product.accessories.map((a) => (
                    <span key={a} className="border border-border bg-forest-deep/60 px-2.5 py-1 font-mono text-[10px] text-sand">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div data-testid="legal-disclaimer-badge" className="disclaimer-box mt-5 flex items-start gap-3 px-4 py-3">
              {product.audience === "road" ? (
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyanx" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
              )}
              <p className="font-mono text-[10px] font-medium leading-relaxed tracking-[0.06em] text-bone/90 uppercase">
                {product.disclaimer}
              </p>
            </div>

            <div className="mt-6 border-t border-border pt-5">
              <p className="mb-3 font-mono text-[10px] font-bold tracking-[0.2em] text-cyanx uppercase">
                Enquire about this bike — pricing & next pre-order slots
              </p>
              <EnquiryForm bike={product.name} testPrefix={`enquiry-form-${product.id}`} />
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
